import { useEffect, useRef, useState } from "react";

import { HARD_BREAK_MOBILE_PX } from "@/constants";
import { useGlobalAudioPlayer } from "@/contexts/GlobalAudioPlayerContext";
import useBreakpoint from "@/hooks/useBreakpoint";
import { clamp, cx, map } from "@/utils";
import PlayButton from "@components/PlayButton";
import Typography from "@components/Typography";

import Cover from "../Episode/partials/Cover";

import styles from "./AudioPlayer.module.css";

type AudioPlayerProps = React.HTMLProps<HTMLDivElement>;

const Compact: React.FunctionComponent<
  AudioPlayerProps & { onExpand: () => void }
> = ({ className, onExpand, ...props }) => {
  const {
    audio,
    playing,
    errored,
    loading,
    currentTime = 0,
  } = useGlobalAudioPlayer();

  const [open, setOpen] = useState<boolean>(audio !== null);
  const progress = useRef<HTMLDivElement>(null);

  // Side-effects
  const [prevAudio, setPrevAudio] = useState(audio);
  if (audio !== prevAudio) {
    setPrevAudio(audio);

    if (audio !== null) {
      setOpen(true);
    }
  }

  useEffect(() => {
    if (progress.current && audio) {
      const beginsAt = 0;
      const stopsAt = 1000;

      const percentage = clamp(
        map(currentTime, beginsAt, stopsAt, 0, 100),
        0,
        100
      );
      progress.current.style.transform = `translateX(${percentage}%)`;
    }
  }, [currentTime, audio]);

  return (
    <div
      {...props}
      className={cx(
        styles.root,
        styles.compact,
        { [styles.open]: open },
        className
      )}
      onClick={onExpand}
    >
      <div className={styles.preview}>
        <div className={styles.content}>
          {audio?.episode && (
            <Cover className={styles.cover} episode={audio?.episode} />
          )}
          <div>
            <Typography className={styles.show} block margin="none">
              <strong>{audio?.episode?.show}</strong>
            </Typography>
            <Typography className={styles.title} block margin="none">
              {audio?.episode?.title}
            </Typography>
          </div>
          <PlayButton
            playing={playing}
            error={errored}
            loading={loading}
            className={styles.play}
            monochrome
            onClick={(e) => {
              // prevent click on playbutton opening the expanded version
              e.stopPropagation();
              if (playing) {
                audio?.pause();
              } else {
                audio?.play();
              }
            }}
          />
        </div>
        {/* <div className={styles.dummy}>
          <div className={styles.progress} ref={progress} />
        </div> */}
        {/* {audio?.episode && (
          <Typography variant="small" align="center" block>
            {`${currentPlaylistIndex + 1} ${L.MENTIONS_PLAYER_COUNT_OF} ${
              audioMentions.length
            } ${pluralize(
              audioMentions.length,
              L.MENTIONS_SINGULAR,
              L.MENTIONS_PLURAL
            )}`}
          </Typography>
        )} */}
      </div>
    </div>
  );
};

const AudioPlayer: React.FC<AudioPlayerProps> = (props) => {
  const [_expanded, setExpanded] = useState<boolean>(false);

  return <Compact onExpand={() => setExpanded(true)} {...props} />;
};

const AudioPlayerResponsive = (props: AudioPlayerProps) => {
  const ref = useRef<typeof document.body>(document.body);
  const mobile = useBreakpoint(ref, HARD_BREAK_MOBILE_PX, false);
  return mobile ? <AudioPlayer {...props} /> : null;
};
export default AudioPlayerResponsive;
